"use client"

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import debounce from "lodash.debounce"
import { apiVentas } from "../../services/Axios"
import Loader from "../utils/loader/Loader"
import ConfirmationModal from "../utils/avisos/ConfirmationModal"
import MessageModal from "../utils/avisos/MessageModal"
import EditSaleModal from "./EditSaleModal" // Modal para edición
import PrintInvoiceModal from "../utils/print/PrintModal" // Modal para impresión
import { InvoiceRecipientInfo } from "../utils/modals/SendInvoiceModal";
import PageHeader from "../ui/management/PageHeader"
import { useAuth } from "../../services/AuthContext" 
import StatsCard from "../ui/management/StatsCard"
import Pagination from "../ui/management/Pagination"
import {
  FaMoneyBillWave,
  FaSearch,
  FaFilter,
  FaEdit,
  FaTrash,
  FaCalendarAlt,
  FaHistory,
  FaPrint,
  FaDownload,
  FaArrowUp,
  FaCoins,
} from "react-icons/fa"
import "../../styles/Main.css"

export interface Sale {
  _id: string;
  productos: Array<{
    producto: { nombre: string; valorVenta: number; unidadMedida?: string; },
    cantidad: number,
    valor: number
  }>;
  totalVenta: number;
  dineroRecibido: number;
  cambio: number;
  fechaVenta: string;
  metodoPago: string;
  descuentoPorcentaje: number;
  organization: {
    name: string;
    razonSocial: string;
    NIT: string;
    direccion: string;
    ciudad: string;
    responsableIVA: boolean;
  };
  sucursal: {
    nombre: string;
    direccion: string;
    ciudad: string;
    telefono?: string;
  };
}

function SalesHistory() {
  const { userData } = useAuth()

  // Estados de historial y dashboard
  const [sales, setSales] = useState<Sale[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [totalVendido, setTotalVendido] = useState<number>(0)
  const [salesCount, setSalesCount] = useState<number>(0)

  // Filtros: búsqueda por fechas, método de pago y nombre
  const [startDate, setStartDate] = useState<string>("")
  const [endDate, setEndDate] = useState<string>("")
  const [metodoPagoFilter, setMetodoPagoFilter] = useState<string>("")
  const [searchTerm, setSearchTerm] = useState<string>("")

  // Paginación
  const salesPerPage = 6
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(1)

  // Modales y mensajes
  const [saleToEdit, setSaleToEdit] = useState<Sale | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)

  // Estados para el modal de impresión
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null)
  const [isPrintModalOpen, setIsPrintModalOpen] = useState<boolean>(false)

  // Modales para confirmación y mensajes
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false)
  const [saleToDelete, setSaleToDelete] = useState<string | null>(null)
  const [isMessageModalOpen, setIsMessageModalOpen] = useState<boolean>(false)
  const [messageContent, setMessageContent] = useState<string>("")

  // Ref para el input de búsqueda
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Función para formatear la fecha en formato local
  const formatLocalDate = (dateValue: Date | string) => {
    const options: Intl.DateTimeFormatOptions = {
      timeZone: "America/Bogota",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    }
  
    return new Date(dateValue).toLocaleString("es-CO", options)
  }  

  // Función para obtener ventas con filtros y paginación (sin modificar la lógica)
  const fetchSales = useCallback((page: number = 1) => {
    setIsLoading(true)
    apiVentas.get("/api/sales", {
      params: {
        page,
        limit: salesPerPage,
        metodoPago: metodoPagoFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        searchTerm: searchTerm || undefined,
      },
    })
      .then((response) => {
        const { sales, totalPages, indicadores } = response.data
        setSales(sales)
        setTotalPages(totalPages)
        setTotalVendido(indicadores.totalMontoVentas)
        setSalesCount(indicadores.totalVentas)
      })
      .catch((error) => {
        console.error("Error al obtener ventas:", error)
      })
      .finally(() => setIsLoading(false))
  }, [salesPerPage, metodoPagoFilter, startDate, endDate, searchTerm])

  useEffect(() => {
    fetchSales(currentPage)
  }, [fetchSales, currentPage])

  // Debounce para la búsqueda
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value)
        setCurrentPage(1)
      }, 500),
    []
  )
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value)
  }

  const clearFilters = () => {
    setStartDate("")
    setEndDate("")
    setMetodoPagoFilter("")
    setSearchTerm("")
    setCurrentPage(1)
    fetchSales(1)
    if (searchInputRef.current) {
      searchInputRef.current.value = ""
    }
  }

  // Funciones para el modal de edición de venta
  const handleEditSale = (saleId: string) => {
    const sale = sales.find((s) => s._id === saleId) || null
    setSaleToEdit(sale)
    setIsEditModalOpen(true)
  }

  const handleSaveEditSale = (updatedSale: Sale) => {
    apiVentas.put(`/api/sales/${updatedSale._id}`, updatedSale)
      .then(() => {
        setSales((prevSales) =>
          prevSales.map((s) => (s._id === updatedSale._id ? updatedSale : s))
        )
        setMessageContent("¡La venta se ha guardado exitosamente!")
        setIsMessageModalOpen(true)
      })
      .catch((error) => {
        console.error("Error al actualizar la venta:", error)
        setMessageContent("Error al actualizar la venta.")
        setIsMessageModalOpen(true)
      })
      .finally(() => {
        setIsEditModalOpen(false)
        fetchSales(currentPage)
      })
  }

  // Funciones para la eliminación de venta
  const confirmDeleteSale = (saleId: string) => {
    setSaleToDelete(saleId)
    setIsConfirmationModalOpen(true)
  }

  const handleDeleteSale = () => {
    if (saleToDelete) {
      apiVentas.delete(`/api/sales/${saleToDelete}`)
        .then(() => {
          setMessageContent("¡La venta ha sido eliminada exitosamente!")
          setIsMessageModalOpen(true)
          fetchSales(currentPage)
        })
        .catch((error) => {
          console.error("Error al eliminar la venta:", error)
          setMessageContent("Error al eliminar la venta.")
          setIsMessageModalOpen(true)
        })
        .finally(() => {
          setIsConfirmationModalOpen(false)
          setSaleToDelete(null)
        })
    }
  }

  // Función para manejo del modal de impresión
  const handlePrintInvoice = (sale: Sale) => {
    if (!userData?.organization) {
      console.warn("Organización no disponible");
      return;
    }

    // Combinamos los datos de la venta con los de la organización
    setSelectedSale({
      ...sale,
      organization: {
        name: userData.organization.name || '',
        razonSocial: userData.organization.razonSocial || '',
        NIT: userData.organization.NIT || '',
        direccion: userData.organization.direccion || '',
        ciudad: userData.organization.ciudad || '',
        responsableIVA: userData.organization.responsableIVA ?? false
      }
    });

    setIsPrintModalOpen(true);
  };

  const handleSendInvoiceEmail = async (data: InvoiceRecipientInfo) => {
    if (!selectedSale) { 
      setMessageContent("No hay una factura seleccionada para enviar.");
      setIsMessageModalOpen(true);
      return;
    }

    setIsLoading(true);

    try {
      // Envía todos los datos nuevos al backend
      await apiVentas.post("/api/sales/send-invoice", {
        saleId: selectedSale._id,
        ...data // Esto incluye email, y opcionalmente customerName y customerDocument
      });
      setMessageContent(`Factura enviada exitosamente a ${data.recipientEmail}.`);
      setIsMessageModalOpen(true);
    } catch (error: any) {
      const errorMessage = error.response?.data?.msg || "Error al enviar la factura por correo.";
      setMessageContent(errorMessage);
      setIsMessageModalOpen(true);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const exportSalesToCSV = async () => {
    if (startDate || endDate || metodoPagoFilter || searchTerm) {
      alert("Debes quitar todos los filtros para exportar el historial completo.");
      return;
    }
  
    try {
      const response = await apiVentas.get("/api/sales", {
        params: {
          limit: 10000,
          page: 1,
        },
      });
  
      const exportSales = response.data.sales;
  
      if (!exportSales || exportSales.length === 0) {
        alert("No hay ventas para exportar.");
        return;
      }
  
      const headers = [
        "Fecha de Venta",
        "ID de Venta",
        "Producto",
        "Cantidad",
        "Valor Unitario",
        "Total Producto",
        "Total Venta",
        "Método de Pago",
        "Sucursal",
        "Ciudad"
      ];
  
      const rows: (string | number)[][] = exportSales.flatMap((sale: Sale) =>
        sale.productos.map(p => [
          formatLocalDate(sale.fechaVenta),
          sale._id,
          p.producto.nombre,
          p.cantidad,
          p.producto.valorVenta,
          p.valor,
          sale.totalVenta,
          sale.metodoPago,
          sale.sucursal?.nombre || "",
          sale.sucursal?.ciudad || ""
        ])
      );      
  
      const csvContent = [
        headers.join(","),
        ...rows.map((row) =>
          row
            .map((value) =>
              typeof value === "string"
                ? `"${value.replace(/"/g, '""')}"`
                : value
            )
            .join(",")
        )
      ].join("\n");      
      
      const BOM = "\uFEFF"; // UTF-8 Byte Order Mark
      const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `detalle-ventas-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error al exportar ventas:", error);
      alert("Error al exportar las ventas.");
    }
  };  

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* Page Header */}
      <PageHeader
        title="Historial de Ventas"
        subtitle="Visualiza y administra el historial de ventas"
        icon={FaHistory}
      />

      {/* Barra de Filtros */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        {/* Filtros de fecha */}

        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            <FaFilter className="mr-2 text-[#2d53da]" /> Filtros de Ventas
          </h2>
          <button
            onClick={clearFilters}
            className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200"
          >
            <i className="fa-solid fa-circle-xmark mr-2"></i> Borrar Filtros
          </button>
        </div>

        <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
              Fecha de Inicio
            </label>
            <div className="relative">
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md focus:ring-[#2d53da] focus:border-[#2d53da] transition-colors duration-200 text-gray-900 pl-3 pr-10 py-2"
              />
              <FaCalendarAlt className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
              Fecha de Fin
            </label>
            <div className="relative">
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md focus:ring-[#2d53da] focus:border-[#2d53da] transition-colors duration-200 text-gray-900 pl-3 pr-10 py-2"
              />
              <FaCalendarAlt className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Search Input */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Buscar por nombre"
              onChange={handleSearchChange}
              className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-[#2d53da] focus:border-[#2d53da] transition-colors duration-200"
              ref={searchInputRef}
            />
          </div>

          {/* Filtro: Método de Pago */}
          <div className="relative">
            <select
              value={metodoPagoFilter}
              onChange={(e) => setMetodoPagoFilter(e.target.value)}
              className="pl-3 w-full p-3 border border-gray-300 rounded-lg focus:ring-[#2d53da] focus:border-[#2d53da] transition-colors duration-200"
            >
              <option value="">Método de Pago</option>
              <option value="efectivo">Efectivo</option>
              <option value="tarjeta">Tarjeta</option>
              <option value="transferencia">Transferencia</option>
            </select>
          </div>
        </div>
      </div>

      {/* Dashboard de Indicadores */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatsCard
          title="Cantidad de Ventas"
          value={salesCount.toString()}
          description="Ventas totales"
          icon={FaArrowUp}
          gradient={false}
        />
        <StatsCard
          title="Total Vendido"
          value={`$${totalVendido ? totalVendido.toLocaleString() : 0}`}
          description="Monto total vendido (COP)"
          icon={FaCoins}
          gradient={false}
        />
      </div>

      <div className="flex justify-end mb-4">
      <button
        onClick={() => exportSalesToCSV()}
        disabled={!!startDate || !!endDate || !!metodoPagoFilter || !!searchTerm}
        className={`py-2 px-4 font-semibold rounded-lg shadow flex items-center gap-2 transition 
          ${!!startDate || !!endDate || !!metodoPagoFilter || !!searchTerm
            ? 'bg-gray-400 text-white cursor-not-allowed'
            : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        title="Solo puedes exportar cuando no hay filtros activos"
      >
        <FaDownload className="text-white text-lg" />
        Descargar Mis Ventas
      </button>
      </div>

      {/* Tabla de Ventas */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        {isLoading ? (
          <Loader />
        ) : sales.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Método de Pago
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Venta (COP)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sales.map((sale) => (
                  <tr key={sale._id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">{formatLocalDate(sale.fechaVenta)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{sale.productos[0]?.producto.nombre || "Sin nombre"}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{sale.metodoPago}</td>
                    <td className="px-6 py-4 whitespace-nowrap">${sale.totalVenta.toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          className="bg-yellow-100 text-yellow-600 hover:bg-yellow-200 p-2 rounded-full transition-colors duration-200"
                          onClick={() => handleEditSale(sale._id)}
                          title="Editar Venta"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="bg-red-100 text-red-600 hover:bg-red-200 p-2 rounded-full transition-colors duration-200"
                          onClick={() => confirmDeleteSale(sale._id)}
                          title="Eliminar Venta"
                        >
                          <FaTrash />
                        </button>
                        <button
                          className="bg-green-100 text-green-600 hover:bg-green-200 p-2 rounded-full transition-colors duration-200"
                          onClick={() => handlePrintInvoice(sale)}
                          title="Imprimir Factura"
                        >
                          <FaPrint />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-2 text-center">
            <div className="bg-gray-100 p-4 rounded-full mb-3">
              <FaMoneyBillWave className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No se encontraron ventas</h3>
            <p className="text-gray-500 mb-4">Intenta con otra búsqueda o ajusta los filtros</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {/* Edit Sale Modal */}
      <EditSaleModal
        sale={saleToEdit}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveEditSale}
        isMessageModalOpen={isMessageModalOpen}
        messageContent={messageContent}
        onCloseMessageModal={() => setIsMessageModalOpen(false)}
      />

      {/* Message Modal */}
      <MessageModal
        isOpen={isMessageModalOpen}
        message={messageContent}
        messageType="success"
        onClose={() => setIsMessageModalOpen(false)}
      />

      {/* Confirmation Modal para eliminación */}
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        title="Confirmar eliminación"
        message="¿Estás seguro de que deseas eliminar esta venta?"
        onConfirm={handleDeleteSale}
        onCancel={() => setIsConfirmationModalOpen(false)}
      />

      {/* Print Invoice Modal */}
      {selectedSale && (
        <PrintInvoiceModal
          factura={{
            ...selectedSale,
            productos: selectedSale.productos.map(p => ({
              ...p,
              producto: {
                ...p.producto,
                unidadMedida: p.producto.unidadMedida || 'unidad', 
              }
            }))
          }}
          isOpen={isPrintModalOpen}
          onClose={() => setIsPrintModalOpen(false)}
          onSendEmail={handleSendInvoiceEmail}
        />
      )}
    </div>
  )
}

export default SalesHistory
